import React, { useEffect, useRef } from 'react';
import { Box, Paper, Typography } from '@mui/material';

const containerStyle = {
  width: '100%', 
  height: '250px',
  borderRadius: '4px'
};

const UserLocationMap = ({ latitude, longitude }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  
  // Convert coordinates to numbers - with validation
  const isValidCoords = latitude && longitude && !isNaN(parseFloat(latitude)) && !isNaN(parseFloat(longitude));
  const lat = isValidCoords ? parseFloat(latitude) : 0;
  const lng = isValidCoords ? parseFloat(longitude) : 0;
  
  // Make sure we have valid numbers with fallbacks to safe coordinates 
  const validLat = isFinite(lat) && lat !== 0 ? lat : 55.751244;  // Default to Moscow coordinates
  const validLng = isFinite(lng) && lng !== 0 ? lng : 37.618423;
  
  // Initialize map after component mounts
  useEffect(() => {
    const initMap = () => {
      if (!mapRef.current || mapInstanceRef.current) return;
      
      // Google Maps API key
      const apiKey = 'AIzaSyDex4tG_VmleDHUjc2TuRRFSO6_1sV--hA';
      
      // Load the Google Maps script dynamically
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initGoogleMap`;
      script.async = true;
      
      // Define global callback for Google Maps script
      window.initGoogleMap = () => {
        try {
          // Create map
          const mapPosition = { lat: validLat, lng: validLng };
          const mapOptions = {
            center: mapPosition,
            zoom: 14,
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: true,
            zoomControl: true
          };
          
          mapInstanceRef.current = new window.google.maps.Map(mapRef.current, mapOptions);
          
          // Create marker
          markerRef.current = new window.google.maps.Marker({
            position: mapPosition,
            map: mapInstanceRef.current,
            title: 'User location',
            animation: window.google.maps.Animation.DROP,
            icon: {
              url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
              scaledSize: new window.google.maps.Size(40, 40)
            }
          });
        } catch (error) {
          console.error('Error initializing Google Maps:', error);
        }
      };
      
      // Append script to document
      document.head.appendChild(script);
      
      return () => {
        // Cleanup
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
        delete window.initGoogleMap;
      };
    };
    
    initMap();
  }, [validLat, validLng]);

  // If coordinates are invalid, return early
  if (!isValidCoords) {
    return null;
  }

  return (
    <Paper elevation={2} sx={{ mt: 1 }}>
      <Box sx={{ p: 1 }}>
        <Typography variant="subtitle2" gutterBottom>User Location</Typography>
        <Box 
          ref={mapRef}
          sx={{ 
            ...containerStyle,
            border: '1px solid #ddd',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f8f8f8'
          }}
        >
          <Typography variant="body2" color="text.secondary">Loading map...</Typography>
        </Box>
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption">Lat: {validLat}</Typography>
          <Typography variant="caption">Lng: {validLng}</Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default UserLocationMap;